import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Dumbbell, Target, Calendar, TrendingUp, FileText, ChevronLeft, ChevronRight, DollarSign, BarChart3, X, Plus, CheckCircle2, Circle, MapPin } from 'lucide-react';
import useLocalStorage from './hooks/useLocalStorage';
import { getDaysUntil, getGreeting, formatTime } from './utils';

const MAX_TASK_LENGTH = 200;
const sanitize = (str) => String(str ?? '').replace(/[<>"'`]/g, '').slice(0, MAX_TASK_LENGTH);

// ── Category styles ───────────────────────────────────────────

const getCategoryStyle = (category, source) => {
  if (source === 'schedule') {
    return { assignment: 'cat-exercise', exam: 'bg-orange-100 border-orange-600', event: 'cat-class' }[category] || 'bg-gray-100 border-gray-600';
  }
  return { class: 'cat-class', work: 'cat-work', exercise: 'cat-exercise', personal: 'cat-personal', other: 'cat-other' }[category] || 'cat-other';
};

// ── TodayTimeline ─────────────────────────────────────────────

const TodayTimeline = ({ events, scheduleEvents, setCurrentModule }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const timelineRef = React.useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getTimePosition = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return ((hours * 60 + minutes) / (24 * 60)) * 100;
  };

  const getCurrentTimePosition = () => {
    const h = currentTime.getHours();
    const m = currentTime.getMinutes();
    return getTimePosition(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  };

  useEffect(() => {
    if (!timelineRef.current) return;
    const container = timelineRef.current;
    const targetScroll = (container.scrollHeight * getCurrentTimePosition() / 100) - (container.clientHeight / 2);
    container.scrollTop = Math.max(0, targetScroll);
  }, [currentTime]);

  const todaysItems = useMemo(() => {
    const today = new Date();
    const todayDay = today.getDay();
    const todayStr = today.toISOString().split('T')[0];

    const timetableItems = events
      .filter(e => e.repeatDays?.length > 0 ? e.repeatDays.includes(todayDay) : e.day === todayDay)
      .map(e => ({ ...e, id: `timetable-${e.id}-${todayStr}`, source: 'timetable' }));

    const scheduleItems = (scheduleEvents || [])
      .filter(i => i.date === todayStr && i.time)
      .map(i => {
        const [h, m] = i.time.split(':').map(Number);
        const endTime = `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        return { ...i, id: `schedule-${i.id}`, source: 'schedule', startTime: i.time, endTime, category: i.type };
      });

    return [...timetableItems, ...scheduleItems].sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [events, scheduleEvents]);

  const overlappingGroups = useMemo(() => {
    const groups = [];
    const processed = new Set();
    todaysItems.forEach((item, idx) => {
      if (processed.has(idx)) return;
      const group = [item];
      processed.add(idx);
      for (let i = idx + 1; i < todaysItems.length; i++) {
        if (processed.has(i)) continue;
        const other = todaysItems[i];
        if (group.some(e => other.startTime < e.endTime && other.endTime > e.startTime)) {
          group.push(other);
          processed.add(i);
        }
      }
      groups.push(group);
    });
    return groups;
  }, [todaysItems]);

  const currentTimePos = getCurrentTimePosition();
  const nowStr = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;
  const hours = Array.from({ length: 24 }, (_, i) => i);

  if (todaysItems.length === 0) {
    return (
      <div className="card bg-white">
        <div className="card-header">
          <h3 className="text-lg font-serif text-neutral-900">Today's Schedule</h3>
        </div>
        <div className="p-8 text-center">
          <Clock className="w-12 h-12 mx-auto mb-3 text-neutral-400 border-4 border-neutral-900 p-2" />
          <p className="text-sm text-neutral-600 font-mono">No events scheduled today</p>
          <button onClick={() => setCurrentModule('timetable')} className="mt-4 px-4 py-2 text-xs border-2 border-neutral-900 bg-white text-neutral-900 hover-lift font-mono font-bold uppercase">
            Add Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-white">
      <div className="card-header flex items-center justify-between">
        <h3 className="text-lg font-serif text-neutral-900">Today's Schedule</h3>
        <button onClick={() => setCurrentModule('timetable')} className="text-xs px-3 py-1 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors font-mono font-bold uppercase">
          View All
        </button>
      </div>

      <div className="relative overflow-hidden" style={{ height: '847px' }}>
        <div ref={timelineRef} className="absolute inset-0 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          <div className="relative px-4 py-4" style={{ height: '1500px' }}>

            {hours.map(hour => (
              <div key={hour} className="absolute left-0 right-0 flex items-center" style={{ top: `${getTimePosition(`${hour}:00`)}%` }}>
                <div className="w-12 pl-2 text-xs font-mono font-bold text-neutral-600 -mt-4">
                  {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                </div>
                <div className="flex-1 h-px bg-neutral-300" />
              </div>
            ))}

            <div className="absolute left-12 right-4 top-0 bottom-0">
              {overlappingGroups.map((group) => {
                const groupWidth = 100 / Math.max(group.length, 1);
                return group.map((event, eventIdx) => {
                  const topPos = getTimePosition(event.startTime);
                  const height = getTimePosition(event.endTime) - topPos;
                  const isCurrent = event.startTime <= nowStr && event.endTime > nowStr;
                  const isPast = event.endTime <= nowStr;

                  return (
                    <div
                      key={event.id}
                      className={`absolute border-2 border-neutral-900 p-2 cursor-pointer transition-all ${getCategoryStyle(event.category, event.source)} ${
                        isCurrent ? 'ring-4 ring-neutral-900 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]' :
                        isPast ? 'opacity-50' : 'hover-lift'
                      }`}
                      style={{
                        top: `${topPos}%`,
                        height: `${height}%`,
                        left: `calc(${eventIdx * groupWidth}% + 4px)`,
                        width: `calc(${groupWidth}% - 4px)`,
                        zIndex: group.length - eventIdx,
                      }}
                      onClick={() => setCurrentModule('timetable')}
                    >
                      <div className="flex items-start gap-1 mb-1">
                        {event.source === 'schedule' && (
                          <span className="text-xs">
                            {event.category === 'assignment' ? '📝' : event.category === 'exam' ? '📚' : '📅'}
                          </span>
                        )}
                        <div className="text-xs font-bold font-mono uppercase text-neutral-900 truncate flex-1">{event.title}</div>
                      </div>
                      <div className="text-xs font-mono text-neutral-700 mt-1">{formatTime(event.startTime)}</div>
                      {event.location && (
                        <div className="text-xs font-mono text-neutral-600 truncate flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </div>
                      )}
                      {isCurrent && (
                        <div className="absolute top-1 right-1">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        </div>
                      )}
                    </div>
                  );
                });
              })}
            </div>

            <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: `${currentTimePos}%` }}>
              <div className="flex items-center">
                <div className="w-12 flex items-center justify-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-neutral-900" />
                </div>
                <div className="flex-1 h-1 bg-red-500 border-y-2 border-neutral-900 relative">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 bg-red-500 border-2 border-neutral-900 px-2 py-0.5">
                    <span className="text-xs font-mono font-bold text-white">NOW</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

// ── DayModal ──────────────────────────────────────────────────

const DayModal = ({ modalContent, closeModal, onItemClick }) => {
  if (!modalContent) return null;
  const { date, assignments, events } = modalContent;
  const allItems = [...assignments, ...events].sort((a, b) => new Date(a.deadline || a.date) - new Date(b.deadline || b.date));

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={closeModal}>
      <div className="card bg-white w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b-2 border-neutral-900">
          <h3 className="text-xl font-serif text-neutral-900">
            {date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </h3>
          <button onClick={closeModal} className="p-2 text-neutral-900 hover:bg-neutral-100 border border-neutral-900">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {allItems.length > 0 ? (
            <div className="space-y-3">
              {allItems.map((item, index) => {
                const isAssignment = 'deadline' in item;
                const itemType = isAssignment ? 'Assignment' : item.type === 'exam' ? 'Exam' : 'Event';
                const bgColor = isAssignment ? 'bg-red-100' : item.type === 'exam' ? 'bg-orange-100' : 'bg-blue-100';
                return (
                  <div key={index} className={`border-2 border-neutral-900 p-3 ${bgColor} hover-lift cursor-pointer`} onClick={() => onItemClick(isAssignment)}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold uppercase text-neutral-900">{itemType}</span>
                      {item.course && <span className="text-xs font-mono text-neutral-700">{item.course}</span>}
                    </div>
                    <p className="text-sm font-medium text-neutral-900">{item.title}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-500">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-neutral-400" />
              <p className="text-sm font-mono">Nothing scheduled for this day!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Dashboard ─────────────────────────────────────────────────

const Dashboard = ({ setCurrentModule: setCurrentModuleProp }) => {
  const [internalModule, setInternalModule] = useState('dashboard');
  const setCurrentModule = setCurrentModuleProp || setInternalModule;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [modalContent, setModalContent] = useState(null);
  const [newTaskText, setNewTaskText] = useState('');

  // State
  const [quickTasks,     setQuickTasks] = useLocalStorage('quickTasks',      []);
  const [timetableEvents]               = useLocalStorage('timetableEvents', []);
  const [scheduleItems]                 = useLocalStorage('scheduleItems',   []);
  const [todayStats, setTodayStats]     = useState({ pomodorosCompleted: 0, habitsCompleted: 0, totalHabits: 0, workoutsCompleted: 0 });

  const assignments = useMemo(() => scheduleItems.filter(i => i.type === 'assignment'), [scheduleItems]);
  const eventsExams = useMemo(() => scheduleItems.filter(i => i.type === 'exam' || i.type === 'event'), [scheduleItems]);

  // Stats
  useEffect(() => {
    const today = new Date();
    const todayKey = today.toISOString().split('T')[0];
    const pomodoros        = JSON.parse(localStorage.getItem('pomodoroCompletions') || '[]');
    const habits           = JSON.parse(localStorage.getItem('habits')              || '[]');
    const habitCompletions = JSON.parse(localStorage.getItem('habitCompletions')    || '{}');
    const workouts         = JSON.parse(localStorage.getItem('workoutHistory')      || '[]');
    const todayHabits      = habitCompletions[todayKey] || {};

    setTodayStats({
      pomodorosCompleted: pomodoros.filter(p => new Date(p.timestamp).toDateString() === today.toDateString()).length,
      habitsCompleted:    Object.values(todayHabits).filter(v => v === true || (typeof v === 'number' && v > 0)).length,
      totalHabits:        habits.length,
      workoutsCompleted:  workouts.filter(w => new Date(w.startTime).toDateString() === today.toDateString()).length,
    });
  }, []);

  // Calendar
  const { daysInMonth, startingDayOfWeek, year, month } = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    return { daysInMonth: new Date(y, m + 1, 0).getDate(), startingDayOfWeek: new Date(y, m, 1).getDay(), year: y, month: m };
  }, [currentDate]);

  const getItemsForDate = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return {
      assignments: assignments.filter(a => (a.deadline || a.date) === dateStr),
      events:      eventsExams.filter(e => e.date === dateStr),
    };
  };

  const upcomingItems = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [
      ...assignments.filter(a => (a.deadline || a.date) && new Date(a.deadline || a.date) >= today && a.status !== 'completed'),
      ...eventsExams.filter(e => e.date && new Date(e.date) >= today),
    ]
      .sort((a, b) => new Date(a.deadline || a.date) - new Date(b.deadline || b.date))
      .slice(0, 5);
  }, [assignments, eventsExams]);

  // Tasks
  const addQuickTask = (e) => {
    e.preventDefault();
    const text = sanitize(newTaskText);
    if (!text.trim()) return;
    setQuickTasks([...quickTasks, { id: Date.now(), text, completed: false }]);
    setNewTaskText('');
  };

  const toggleTask = (id) => setQuickTasks(quickTasks.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  const deleteTask = (id) => setQuickTasks(quickTasks.filter(t => t.id !== id));

  // Nav
  const goToPreviousMonth = () => { const d = new Date(currentDate); d.setMonth(d.getMonth() - 1); setCurrentDate(d); };
  const goToNextMonth     = () => { const d = new Date(currentDate); d.setMonth(d.getMonth() + 1); setCurrentDate(d); };
  const goToToday         = () => setCurrentDate(new Date());

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const dayNames   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  return (
    <div className="page flex flex-col lg:flex-row">
      <DayModal
        modalContent={modalContent}
        closeModal={() => setModalContent(null)}
        onItemClick={() => { setModalContent(null); setCurrentModule('schedule'); }}
      />

      {/* Sidebar */}
      <div className="w-full lg:w-64 bg-white border-b-2 lg:border-b-0 lg:border-r-2 border-neutral-900 flex-shrink-0 lg:h-screen lg:sticky lg:top-0 lg:overflow-hidden flex flex-col">
        <div className="p-4 sm:p-6 flex-shrink-0">
          <h1 className="text-2xl font-serif text-neutral-900 tracking-tight">STUDIUM</h1>
          <p className="text-neutral-600 text-xs mt-1 font-mono uppercase tracking-wider">Productivity Suite</p>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 pb-4 sm:pb-6">
          <nav className="mb-6">
            <div className="flex flex-row lg:flex-col gap-2 whitespace-nowrap lg:whitespace-normal">
              {[
                { id: 'dashboard',  label: 'Overview',   Icon: TrendingUp },
                { id: 'pomodoro',   label: 'Pomodoro',   Icon: Clock },
                { id: 'schedule',   label: 'Schedule',   Icon: FileText },
                { id: 'timetable',  label: 'Timetable',  Icon: Calendar },
                { id: 'finance',    label: 'Finance',    Icon: DollarSign },
                { id: 'workout',    label: 'Workouts',   Icon: Dumbbell },
                { id: 'habits',     label: 'Habits',     Icon: Target },
                { id: 'statistics', label: 'Statistics', Icon: BarChart3 },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => setCurrentModule(id)}
                  className={`w-auto lg:w-full flex items-center gap-3 px-4 py-3 border-2 border-neutral-900 transition-all ${
                    id === 'dashboard' ? 'bg-neutral-900 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-white text-neutral-900 hover-lift'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </div>
          </nav>

          <div className="pt-6 border-t-2 border-neutral-900 hidden lg:block">
            <div className="text-xs uppercase tracking-wider font-mono text-neutral-900 mb-3">Today's Progress</div>
            <div className="space-y-2">
              {[
                { label: 'Pomodoros', value: todayStats.pomodorosCompleted },
                { label: 'Habits',    value: `${todayStats.habitsCompleted}/${todayStats.totalHabits}` },
                { label: 'Workouts',  value: todayStats.workoutsCompleted },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between text-sm border border-neutral-300 p2">
                  <span className="text-neutral-600 font-mono">{label}</span>
                  <span className="font-bold text-neutral-900">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1">
        <div className="max-w-7xl mx-auto p-4 sm:p-8">

          <div className="mb-8">
            <h2 className="text-3xl sm:text-4xl font-serif text-neutral-900 mb-2">Good {getGreeting()}</h2>
            <p className="text-neutral-600 font-mono text-sm">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {[
              { Icon: Clock,    label: 'Focus sessions',   value: todayStats.pomodorosCompleted },
              { Icon: Target,   label: 'Habits completed', value: `${todayStats.habitsCompleted}/${todayStats.totalHabits}` },
              { Icon: Dumbbell, label: 'Workouts done',    value: todayStats.workoutsCompleted },
            ].map(({ Icon, label, value }) => (
              <div key={label} className="card-sm bg-white p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-5 h-5 text-neutral-900" />
                  <span className="text-3xl font-bold text-neutral-900">{value}</span>
                </div>
                <div className="text-sm text-neutral-600 font-mono uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            <div className="lg:col-span-2">

              {/* Calendar */}
              <div className="card bg-white">
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between mb-4 gap-4">
                    <h3 className="text-2xl font-serif text-neutral-900">{monthNames[month]} {year}</h3>
                    <div className="flex items-center justify-between sm:justify-start sm:gap-2">
                      <button onClick={goToPreviousMonth} className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                      <button onClick={goToToday} className="px-3 py-2 text-sm border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors font-mono uppercase">Today</button>
                      <button onClick={goToNextMonth} className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"><ChevronRight className="w-5 h-5" /></button>
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                    {dayNames.map(day => (
                      <div key={day} className="text-center text-xs uppercase tracking-wider font-mono text-neutral-900 py-2 border border-neutral-300">{day}</div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1 sm:gap-2">
                    {Array.from({ length: startingDayOfWeek }).map((_, i) => <div key={`e-${i}`} className="aspect-square" />)}
                    {Array.from({ length: daysInMonth }).map((_, idx) => {
                      const day = idx + 1;
                      const date = new Date(year, month, day);
                      const isToday = date.toDateString() === new Date().toDateString();
                      const isPast  = date < new Date().setHours(0, 0, 0, 0);
                      const { assignments: da, events: de } = getItemsForDate(date);

                      return (
                        <div
                          key={day}
                          onClick={() => setModalContent({ date, ...getItemsForDate(date) })}
                          className={`aspect-square border-2 p-1 sm:p-2 relative cursor-pointer transition-all ${
                            isToday ? 'border-neutral-900 bg-neutral-900 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]'
                            : isPast ? 'border-neutral-300 bg-neutral-100 text-neutral-400'
                            : 'border-neutral-900 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-0.5 hover:-translate-y-0.5'
                          }`}
                        >
                          <div className={`text-xs sm:text-sm font-bold ${isToday ? 'text-white' : 'text-neutral-900'}`}>{day}</div>
                          {(da.length > 0 || de.length > 0) && (
                            <div className="absolute bottom-1 sm:bottom-2 left-1 right-1 flex items-center justify-center gap-1">
                              {da.length > 0 && <div className="w-2 h-2 bg-red-500 border border-neutral-900" />}
                              {de.length > 0 && <div className="w-2 h-2 bg-blue-500 border border-neutral-900" />}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-6 mt-4 pt-4 border-t-2 border-neutral-900">
                    {[['bg-red-500','Assignments'],['bg-blue-500','Events/Exams']].map(([color, label]) => (
                      <div key={label} className="flex items-center gap-2">
                        <div className={`w-3 h-3 ${color} border border-neutral-900`} />
                        <span className="text-xs text-neutral-900 font-mono uppercase">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Upcoming + Tasks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">

                <div className="card bg-white">
                  <div className="card-header">
                    <h3 className="text-lg font-serif text-neutral-900">Upcoming</h3>
                  </div>
                  <div className="p-4 max-h-96 overflow-y-auto">
                    {upcomingItems.length === 0 ? (
                      <div className="text-center py-8 text-neutral-500">
                        <Calendar className="w-8 h-8 mx-auto mb-2 text-neutral-400" />
                        <p className="text-sm font-mono">Nothing upcoming</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {upcomingItems.map(item => {
                          const isAssignment = 'deadline' in item;
                          const daysUntil = getDaysUntil(item.deadline || item.date);
                          return (
                            <div key={item.id} className="border-2 border-neutral-900 p-3 hover-lift cursor-pointer" onClick={() => setCurrentModule('schedule')}>
                              <div className="flex items-start gap-2 mb-1">
                                <div className={`w-2 h-2 border border-neutral-900 flex-shrink-0 mt-1.5 ${isAssignment ? 'bg-red-500' : item.type === 'exam' ? 'bg-orange-500' : 'bg-blue-500'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-neutral-900 truncate">{item.title}</div>
                                  <div className="text-xs text-neutral-600 font-mono">
                                    {daysUntil === 0 ? 'TODAY' : daysUntil === 1 ? 'TOMORROW' : `IN ${daysUntil} DAYS`}
                                  </div>
                                  {item.course && <div className="text-xs text-neutral-600 mt-1 font-mono">{item.course}</div>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="card bg-white">
                  <div className="card-header">
                    <h3 className="text-lg font-serif text-neutral-900">Quick Tasks</h3>
                  </div>
                  <div className="p-4">
                    <form onSubmit={addQuickTask} className="mb-4">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTaskText}
                          onChange={e => setNewTaskText(e.target.value)}
                          maxLength={MAX_TASK_LENGTH}
                          placeholder="Add a quick task..."
                          className="flex-1 px-3 py-2 border-2 border-neutral-900 font-mono text-sm focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                        />
                        <button type="submit" className="px-4 py-2 bg-neutral-900 text-white border-2 border-neutral-900 hover:bg-neutral-700 transition-colors">
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                    </form>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {quickTasks.length === 0 ? (
                        <div className="text-center py-4 text-neutral-500">
                          <p className="text-sm font-mono">No tasks yet</p>
                        </div>
                      ) : (
                        quickTasks.map(task => (
                          <div key={task.id} className="flex items-center gap-2 p-2 border border-neutral-300 hover:border-neutral-900 transition-colors group">
                            <button onClick={() => toggleTask(task.id)} className="flex-shrink-0">
                              {task.completed ? <CheckCircle2 className="w-5 h-5 text-neutral-900" /> : <Circle className="w-5 h-5 text-neutral-400" />}
                            </button>
                            <span className={`flex-1 text-sm font-mono ${task.completed ? 'line-through text-neutral-400' : 'text-neutral-900'}`}>
                              {task.text}
                            </span>
                            <button onClick={() => deleteTask(task.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-neutral-900">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Timeline */}
            <TodayTimeline
              events={timetableEvents}
              scheduleEvents={[...assignments, ...eventsExams]}
              setCurrentModule={setCurrentModule}
            />

          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;